import { useEffect, useState, useCallback } from 'react'
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { getListingsByIds } from '@/services/api/listings'
import type { Listing } from '@/types/listing'

export function useSavedItems() {
  const { user, isAuthenticated } = useAuthStore()
  const { showToast, openModal } = useUIStore()
  const [savedIds, setSavedIds] = useState<string[]>([])
  const [savedListings, setSavedListings] = useState<Listing[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Subscribe to saved item IDs
  useEffect(() => {
    if (!user) {
      setSavedIds([])
      setSavedListings([])
      setIsLoading(false)
      return
    }

    const savedRef = collection(db, 'users', user.uid, 'saved')
    const unsubscribe = onSnapshot(savedRef, (snapshot) => {
      const ids = snapshot.docs.map((doc) => doc.id)
      setSavedIds(ids)
      setIsLoading(false)
    })

    return () => unsubscribe()
  }, [user])

  // Load full listing data for saved items
  useEffect(() => {
    if (savedIds.length === 0) {
      setSavedListings([])
      return
    }

    getListingsByIds(savedIds)
      .then(setSavedListings)
      .catch(console.error)
  }, [savedIds])

  const isSaved = useCallback(
    (listingId: string) => savedIds.includes(listingId),
    [savedIds]
  )

  const toggleSave = useCallback(
    async (listingId: string) => {
      if (!isAuthenticated || !user) {
        openModal('login')
        return
      }

      const savedRef = doc(db, 'users', user.uid, 'saved', listingId)

      try {
        if (isSaved(listingId)) {
          await deleteDoc(savedRef)
          showToast('Removed from watchlist')
        } else {
          await setDoc(savedRef, { savedAt: serverTimestamp() })
          showToast('Added to watchlist!')
        }
      } catch (error) {
        console.error('Error toggling save:', error)
        showToast('Failed to update watchlist', 'error')
      }
    },
    [user, isAuthenticated, isSaved, openModal, showToast]
  )

  return {
    savedIds,
    savedListings,
    isLoading,
    isSaved,
    toggleSave,
  }
}
